import React from 'react'

export interface PreviewToggleProps {
  /** Toggles the preview; omit to render `children` with no wrapper at all (nothing to toggle). */
  onToggle: (() => void | Promise<void>) | undefined
  isBusy: boolean
  isPreviewVisible: boolean
  children: React.ReactNode
}

/**
 * Makes a locked thumbnail clickable to toggle its preview. Uses a
 * `role="button"` div rather than a native `<button>` because the thumbnail
 * it wraps can itself contain interactive children (gallery carousel arrows,
 * a Composer dismiss X) — nesting a real `<button>` around those would
 * produce invalid, inaccessible nested-interactive-element markup.
 */
const PreviewToggle: React.FC<PreviewToggleProps> = ({
  onToggle,
  isBusy,
  isPreviewVisible,
  children,
}) => {
  if (!onToggle) return <React.Fragment>{children}</React.Fragment>

  return (
    <div
      role="button"
      tabIndex={isBusy ? -1 : 0}
      aria-label="Toggle preview"
      aria-busy={isBusy}
      aria-pressed={isPreviewVisible}
      aria-disabled={isBusy || undefined}
      onClick={isBusy ? undefined : onToggle}
      onKeyDown={(e) => {
        if (isBusy) return
        // Only handle keys that originate on the wrapper itself, not on
        // inner interactive elements (dismiss X, carousel arrows). Without
        // this guard, pressing Enter/Space on a focused child would bubble
        // up and toggle the preview in addition to activating the child.
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          void onToggle()
        }
      }}
      className={
        !isBusy
          ? 'block w-full cursor-pointer text-left'
          : 'block w-full text-left'
      }
    >
      {children}
    </div>
  )
}

export default PreviewToggle
